//src/components/MarketOverview.tsx

import { Card, CardContent } from "@/components/ui/card";
import {
  useMarketHealth,
  useGateDecision,
  useCoreAssets,
  useSentiment,
  useOrderbook,
  useMovers,
  useSessionPulse,
} from "@/hooks/useMarket";
import type { CoreAssetRow } from "@/types/market";
import { useLiveMarket } from "@/lib/liveMarket";

export function MarketOverview() {
  const { data: health, isLoading: healthLoading } = useMarketHealth();
  const { data: gate } = useGateDecision();
  const { data: coreAssets, isLoading: coreLoading } = useCoreAssets();
  const { data: sentiment } = useSentiment();
  const { data: movers } = useMovers();
  const { data: session, error: sessionError, isLoading: sessionLoading } = useSessionPulse();
  const { data: obBtc } = useOrderbook("BTCUSDT");
  const { data: obEth } = useOrderbook("ETHUSDT");

  const btc = coreAssets?.core_assets?.BTCUSDT;
  const eth = coreAssets?.core_assets?.ETHUSDT;

  // Establish websocket live updates
  useLiveMarket();

  if (session && typeof window !== 'undefined') {
    // Debug log (remove in production if noisy)
    // eslint-disable-next-line no-console
    console.debug('[session API]', session);
  }
  if (sessionError && typeof window !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn('[session API error]', sessionError);
  }

  return (
    <div className="space-y-4">
      <MarketHealthBar data={health} loading={healthLoading} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CoreAssetCard symbol="BTC" row={btc} loading={coreLoading} />
        <CoreAssetCard symbol="ETH" row={eth} loading={coreLoading} />
      </div>
      <PositioningStrip sentiment={sentiment} session={session} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MarketMovers data={movers} />
        <SessionPulse data={session} loading={sessionLoading} error={sessionError as any} />
        <div className="space-y-4">
          <OrderbookSnapshot symbol="BTC" data={obBtc} />
            <OrderbookSnapshot symbol="ETH" data={obEth} />
        </div>
      </div>
      {sessionError && (
        <div className="text-xs text-red-400 border border-red-700/40 rounded p-2">Session load failed: {(sessionError as any).message || 'parse error'}</div>
      )}
      {sessionLoading && !session && (
        <div className="text-xs text-gray-400 italic">Loading session…</div>
      )}
      <PreAnalysisGate data={gate} />
    </div>
  );
}

// --- Presentational components (visual-only placeholders) ---
function MarketHealthBar({ data, loading }: { data?: any; loading?: boolean }) {
  const composite = data?.composite_score ?? 0;
  const status = data?.status || "—";
  const barPct = Math.round((composite || 0) * 100);
  return (
    <div className="w-full rounded-lg bg-[#071018]/80 backdrop-blur border border-[#1E2A44] p-2 sm:p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
      <div className="flex-1 w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="text-xs text-gray-400">Status</div>
            <div className={`px-2 py-0.5 rounded-md text-[12px] font-semibold border ${status === 'DEAD' ? 'bg-red-600/10 border-red-700 text-red-300' : 'bg-emerald-600/10 border-emerald-700 text-emerald-300'}`}>
              {loading ? '...' : status}
            </div>
            <div className="text-xs text-gray-400 ml-0 sm:ml-4">Composite</div>
            <div className="w-20 sm:w-28 h-2 bg-[#050C14] border border-[#233044] rounded-full overflow-hidden ml-1">
              <div className="h-2 bg-emerald-400 transition-all" style={{ width: `${barPct}%` }} />
            </div>
            <div className="text-xs text-gray-400 ml-1">({composite?.toFixed?.(2) ?? '—'})</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CoreAssetCard({ symbol, row, loading }: { symbol: string; row?: CoreAssetRow; loading?: boolean }) {
  const change = row?.change_24h;
  const changeFmt = change === undefined || change === null ? '—' : `${change > 0 ? '+' : ''}${(change * 100).toFixed(2)}%`;
  const momentum = row?.momentum;
  const momentumText = momentum ? `EMA ${momentum.status} · RSI ${momentum.rsi ?? '—'}` : '—';
  return (
    <Card className="bg-[#0D1724]/80 backdrop-blur border border-[#1E2A44] shadow-sm">
      <CardContent className="p-2 sm:p-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <div className="text-sm text-gray-400">{symbol}</div>
            <div className="flex items-baseline gap-2">
              <div className="text-lg sm:text-xl font-semibold text-white">{loading ? '…' : row?.price ?? '—'}</div>
              <div className={`text-sm ${change && change > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{loading ? '…' : changeFmt}</div>
            </div>
          </div>
          <div className="text-right text-xs text-gray-400">
            <div>Support: {row?.levels?.support ?? '—'}</div>
            <div>Resistance: {row?.levels?.resistance ?? '—'}</div>
          </div>
        </div>
        <div className="mt-2 text-xs sm:text-sm text-gray-300">
          Momentum: {momentumText}
        </div>
        {row?.risk && (
          <div className="mt-2 text-[12px] text-rose-300">
            Risk: {row.risk}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PositioningStrip({ sentiment, session }: { sentiment?: any; session?: any }) {
  // Fallback extraction for possible backend schema variants
  const dominanceRaw = (
    sentiment?.btc_dominance ??
    sentiment?.btc_dominance_pct ??
    sentiment?.btc_dominance_ratio ??
    sentiment?.dominance?.btc ??
    null
  );
  const dominance = dominanceRaw != null ? Math.min(Math.round(Number(dominanceRaw)), 100) : 0;
  const corrRaw = (
    sentiment?.btc_eth_correlation ??
    sentiment?.btc_eth_corr ??
    sentiment?.correlation?.btc_eth ??
    sentiment?.correlations?.btc_eth ??
    null
  );
  const corr = corrRaw != null ? Number(corrRaw) : null;
  const corrFmt = corr == null ? '—' : `${( (corr > 1 ? corr : corr) * 100).toFixed(0)}%`;
  const fundingRaw = (
    sentiment?.funding_rate?.btc ??
    sentiment?.funding?.btc ??
    sentiment?.funding_rate_btc ??
    sentiment?.funding_btc ??
    null
  );
  const funding = fundingRaw != null ? Number(fundingRaw) : null;
  const fundingFmt = funding == null ? '—' : `${(funding * 100).toFixed(4)}%`;
  if (typeof window !== 'undefined' && sentiment && (dominanceRaw == null || corrRaw == null || fundingRaw == null)) {
    // eslint-disable-next-line no-console
    console.debug('[sentiment fallback debug]', { keys: Object.keys(sentiment||{}), dominanceRaw, corrRaw, fundingRaw });
  }
  return (
    <div className="w-full rounded-lg bg-[#071018]/80 backdrop-blur border border-[#1E2A44] p-2 sm:p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs text-gray-300 gap-2">
      <div className="flex items-center gap-3 flex-wrap w-full">
        <div className="flex flex-col">
          <span className="text-[11px] text-gray-400">BTC Dominance</span>
          <div className="w-32 sm:w-36 h-2 bg-[#050C14] border border-[#233044] rounded-full overflow-hidden mt-1">
            <div className="h-2 bg-emerald-400 transition-all" style={{ width: `${dominance}%` }} />
          </div>
        </div>
        <div className="text-xs">
          BTC–ETH Corr: <span className="text-white">{corrFmt}</span>
        </div>
        <div className="text-xs">
          Funding:{" "}
            <span className="px-2 py-0.5 rounded text-sm bg-green-600/10 border border-green-600 text-emerald-300">
              {fundingFmt}
            </span>
        </div>
        <div className="text-xs">
          Session: <span className="text-emerald-300">{session?.session || '—'}</span>
        </div>
      </div>
    </div>
  );
}

function MarketMovers({ data }: { data?: any }) {
  const gainers: any[] = data?.movers?.gainers || [];
  const losers: any[] = data?.movers?.losers || [];
  const allSymbols: any[] = data?.movers?.all_symbols || [];
  const pool = [...gainers, ...losers, ...allSymbols];

  const targetOrder = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "DOGEUSDT", "XRPUSDT"];

  const items = targetOrder.map(sym => {
    const found = pool.find(x => (x.symbol || x.t) === sym) || {};
    const raw = typeof found.change_24h === 'number' ? found.change_24h : (found.change_pct ?? found.change);
    const ch = (raw === null || raw === undefined || isNaN(raw))
      ? '—'
      : `${raw > 0 ? '+' : ''}${Number(raw).toFixed(2)}%`;
    return { t: sym, ch };
  });

  return (
    <Card className="bg-[#0D1724]/80 backdrop-blur border border-[#1E2A44] shadow-sm">
      <CardContent className="p-2 sm:p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">Market Movers</div>
          <div className="text-xs text-gray-400">Top</div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm min-h-20">
          {items.length === 0 && (
            <div className="col-span-2 text-xs text-gray-500">Loading movers…</div>
          )}
          {items.map((it: any, i: number) => (
            <div
              key={i}
              className={`flex items-center justify-between p-2 rounded border border-[#1E2A44] ${
                it.ch.startsWith('+') ? 'bg-emerald-500/5' : it.ch.startsWith('-') ? 'bg-red-500/5' : 'bg-[#132434]'
              }`}
            >
              <div className="font-medium text-gray-100">{it.t}</div>
              <div
                className={
                  it.ch.startsWith('+') ? 'text-emerald-300' : it.ch.startsWith('-') ? 'text-rose-300' : 'text-gray-300'
                }
              >
                {it.ch}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SessionPulse({ data, loading, error }: { data?: any; loading?: boolean; error?: any }) {
  const rawSession = data?.session ?? data?.current_session;
  const current = typeof rawSession === 'string' ? rawSession : '—';
  return (
    <Card className="bg-[#0D1724]/80 backdrop-blur border border-[#1E2A44] shadow-sm">
      <CardContent className="p-2 sm:p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-400">Session Pulse</div>
          {loading && <div className="text-[10px] text-gray-500">loading…</div>}
          {error && <div className="text-[10px] text-rose-400">err</div>}
        </div>
        <div className="flex flex-col gap-2">
          <SessionRow
            dot={'bg-emerald-400'}
            label="Current:"
            badge={current || 'Unknown'}
            badgeClass='bg-rose-600/10 border border-rose-600 text-rose-300'
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SessionRow({
  dot,
  label,
  badge,
  badgeClass
}: {
  dot: string;
  label: string;
  badge: string;
  badgeClass: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full block ${dot}`} />
      <span className="text-sm text-gray-200">{label}</span>
      <span className={`ml-auto text-xs px-2 py-0.5 rounded ${badgeClass}`}>
        {badge}
      </span>
    </div>
  );
}

function OrderbookSnapshot({ symbol, data }: { symbol: string; data?: any }) {
  return (
    <Card className="bg-[#0D1724]/80 backdrop-blur border border-[#1E2A44] shadow-sm">
      <CardContent className="p-2 sm:p-3">
        <div className="flex items-center">
          <div className="text-sm text-gray-400">{symbol} Orderbook</div>
        </div>
        <div className="mt-1 text-xs text-gray-300">
          Imbalance: Buy {data?.imbalance?.buy_percentage != null ? `${(data.imbalance.buy_percentage*100).toFixed(0)}%` : '—'} / Sell {data?.imbalance?.sell_percentage != null ? `${(data.imbalance.sell_percentage*100).toFixed(0)}%` : '—'}
        </div>
        <div className="mt-1 text-sm text-emerald-300">Execution: {data?.execution || '—'}</div>
      </CardContent>
    </Card>
  );
}

function PreAnalysisGate({ data }: { data?: any }) {
  const status = data?.status || data?.gate_decision?.status;
  const action = data?.action || data?.gate_decision?.action;
  const reasons: string[] = data?.reasons || data?.gate_decision?.reasons || [];
  const isNoTrade = (status || '').toUpperCase().includes('NO');
  // Suppress the CAUTION / WAIT - Monitor conditions block per user request
  if (
    (status || '').toUpperCase() === 'CAUTION' &&
    (action || '').toUpperCase().startsWith('WAIT')
  ) {
    return null;
  }
  const wrapperCls = isNoTrade
    ? 'border border-red-600/60 bg-red-900/10 text-red-300'
    : 'border border-emerald-600/60 bg-emerald-900/10 text-emerald-300';
  return (
    <div className={`w-full rounded-lg backdrop-blur p-2 sm:p-3 text-sm ${wrapperCls}`}>        
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div className="font-semibold">
          {status || '—'}{reasons[0] ? `: ${reasons[0]}` : ''}
        </div>
        <div className="text-xs text-gray-400">Action: {action || '—'}</div>
      </div>
      {reasons.length > 1 && (
        <ul className="mt-2 list-disc list-inside text-xs text-gray-400">
          {reasons.slice(1).map((r,i)=>(<li key={i}>{r}</li>))}
        </ul>
      )}
    </div>
  );
}